// Task #3971 — One-shot, same-tab signal that the user just finished onboarding.
//
// After onboarding completes, the dashboard's Team Inbox should default its
// assignment filter to "Assigned to me" so the freshly-seeded setup checklist is
// front and center — but ONLY on that very first post-onboarding load, not on
// every subsequent dashboard visit. We can't read that from the server cheaply
// per-render, so the welcome page drops a transient flag in `sessionStorage`
// right before navigating to the dashboard, and the inbox consumes (reads +
// clears) it once on mount.
//
// sessionStorage (not localStorage) keeps this scoped to the current tab/session
// and naturally clears when the tab closes; consuming it deletes it so a manual
// refresh of the dashboard no longer forces the "mine" default.
export const ONBOARDING_JUST_COMPLETED_KEY = "padpulse:onboardingJustCompleted";

function getSessionStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    // Access can throw in some privacy modes / sandboxed iframes.
    return null;
  }
}

/** Mark that onboarding just finished. Call right before navigating to the dashboard. */
export function markOnboardingJustCompleted(): void {
  const store = getSessionStorage();
  if (!store) return;
  try {
    store.setItem(ONBOARDING_JUST_COMPLETED_KEY, "1");
  } catch {
    // Best-effort only — the inbox simply falls back to its normal default.
  }
}

/**
 * Read and clear the "just completed onboarding" flag. Returns true only the
 * first time it is called after {@link markOnboardingJustCompleted}; subsequent
 * calls (and plain dashboard visits) return false.
 */
export function consumeOnboardingJustCompleted(): boolean {
  const store = getSessionStorage();
  if (!store) return false;
  try {
    const flagged = store.getItem(ONBOARDING_JUST_COMPLETED_KEY) === "1";
    if (flagged) store.removeItem(ONBOARDING_JUST_COMPLETED_KEY);
    return flagged;
  } catch {
    return false;
  }
}
